import Decoders from '../Models/decoders';
import {AssessmentResultSummary, ProviderAssessmentRecord} from './Assessment';
import Storage from './Storage';

export const EXPORT_HISTORY_STORAGE_KEY = 'adapt-mh.exports.history';

type ExportHistoryRecord = {
  fileName: string;
  exportedAt: string;
  kind: 'adolescent-summary' | 'provider-json' | 'provider-csv';
};

const sanitizeFileName = (value: string): string => {
  return value.replace(/[^a-z0-9-_]+/gi, '-').replace(/^-+|-+$/g, '');
};

const saveExportHistory = async (record: ExportHistoryRecord): Promise<void> => {
  const rawValue = await Storage.get(EXPORT_HISTORY_STORAGE_KEY);
  let existingRecords: ExportHistoryRecord[] = [];

  if (rawValue) {
    try {
      const parsedRecords = JSON.parse(rawValue) as ExportHistoryRecord[];
      existingRecords = Array.isArray(parsedRecords) ? parsedRecords : [];
    } catch {
      existingRecords = [];
    }
  }

  await Storage.set(EXPORT_HISTORY_STORAGE_KEY, JSON.stringify([record, ...existingRecords]));
};

const downloadFile = async (
  fileName: string,
  content: string,
  kind: ExportHistoryRecord['kind'],
  mimeType: string,
): Promise<string> => {
  const blob = new Blob([content], {type: `${mimeType};charset=utf-8`});
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);

  await saveExportHistory({fileName, exportedAt: new Date().toISOString(), kind});
  return fileName;
};

export const shareAdolescentSummary = async (summary: AssessmentResultSummary): Promise<string> => {
  const content = [
    'ADAPT-MH private self-check summary',
    '',
    summary.headline,
    summary.explanation,
    '',
    `Suggested next step: ${summary.suggestedAction}`,
    '',
    'This is not a diagnosis. It does not include raw model probabilities.',
  ].join('\n');
  const fileName = `${sanitizeFileName(summary.sessionId)}-summary.txt`;

  return downloadFile(fileName, content, 'adolescent-summary', 'text/plain');
};

const toCsvCell = (value: string | number | undefined): string => {
  const safeValue = value === undefined ? '' : String(value);
  return `"${safeValue.replace(/"/g, '""')}"`;
};

export const shareProviderExport = async (
  record: ProviderAssessmentRecord,
  format: 'json' | 'csv',
): Promise<string> => {
  const {resultSummary} = record;
  const probabilityEntries = Object.entries(resultSummary.predictions).map(([key, value]) => ({
    key,
    label: Decoders[key as keyof typeof Decoders] || key,
    probability: value,
  }));

  if (format === 'json') {
    const fileName = `${sanitizeFileName(record.sessionId)}-provider-summary.json`;
    const content = JSON.stringify(
      {
        sessionId: record.sessionId,
        completedAt: resultSummary.completedAt,
        mode: record.mode,
        language: record.language,
        participantAlias: record.participantAlias,
        sharingConsent: record.sharingConsent,
        severityLevel: resultSummary.severityLevel,
        headline: resultSummary.headline,
        suggestedAction: resultSummary.suggestedAction,
        riskFlag: resultSummary.riskFlag,
        probabilities: probabilityEntries,
        providerAction: record.providerAction,
        referralStatus: record.referralStatus,
        providerNotes: record.providerNotes,
        followUps: record.followUps || [],
        ehrIntegration: 'deferred',
      },
      null,
      2,
    );

    return downloadFile(fileName, content, 'provider-json', 'application/json');
  }

  const fileName = `${sanitizeFileName(record.sessionId)}-provider-summary.csv`;
  const content = [
    [
      'sessionId',
      'completedAt',
      'mode',
      'language',
      'severityLevel',
      'riskFlag',
      'suggestedAction',
      'providerAction',
      'referralStatus',
      'providerNotes',
      ...probabilityEntries.map((entry) => entry.label),
    ]
      .map(toCsvCell)
      .join(','),
    [
      record.sessionId,
      resultSummary.completedAt,
      record.mode,
      record.language,
      resultSummary.severityLevel,
      resultSummary.riskFlag.triggered ? 'urgent' : 'none',
      resultSummary.suggestedAction,
      record.providerAction?.label,
      record.referralStatus,
      record.providerNotes,
      ...probabilityEntries.map((entry) => entry.probability.toFixed(4)),
    ]
      .map(toCsvCell)
      .join(','),
  ].join('\n');

  return downloadFile(fileName, content, 'provider-csv', 'text/csv');
};
